import Axios from 'axios';
import React from 'react';
import Joi from '@hapi/joi';

import TrackableReact from '../trackableReact';
import ErrorToast from './errorToast';
import { sanitizeNextPath, destinationToPath, resolveAidFailureReturnUrl } from '../../safe_redirect';
import { getSsoRedirectResult, signInToFirebase, signInWithSso, signOutOfFirebase } from '../firebaseFns';
import SecondaryAuthentication from '../secondaryAuthentication';
import { normaliseProviderOptions } from './providerOptions';
import {
  ContinueAsStep,
  DetectingStep,
  EmailLoginForm,
  LoginCard,
  MaintenanceStep,
  OrgPickerStep,
  PasswordLoginForm,
  PasswordResetForm,
  ReturningStep,
  SoftOutageBanner,
  SsoAccountBlocked,
  SsoEmailMismatch,
  SsoFailure,
  SsoRedirect
} from './forms';
import {
  clearPendingSso,
  extractLoginErrorMessage,
  peekPendingSso,
  readPendingSso,
  shouldRejectSsoEmailMismatch,
  storePendingSso
} from '../../login_sso';
import { getRuntimeConfig } from '../runtimeConfig';

const internals = {};
const SSO_REDIRECT_DELAY_MS =
  getRuntimeConfig().deploymentTier === 'production' ? 0 : 5000;

// The "One moment…" spinner only paints if the lookup takes longer than this…
const LOOKUP_SPINNER_AFTER_MS = 400;
// …and once painted it stays up to a fixed floor, so known and unknown
// addresses are indistinguishable from timing alone.
const LOOKUP_FLOOR_MS = 1200;
// A slow lookup falls through to the password step rather than an error.
const LOOKUP_TIMEOUT_MS = 3000;

const SEEN_FLAG = 'audirie.seenLogin';

// Enrollment handle (+ optional destination) from a /enroll link. Stored in
// sessionStorage so it survives the SSO redirect round trip, consumed when
// the login API call resolves (success or failure).
const PENDING_ENROLLMENT_KEY = 'pendingEnrollment';
// Safe `next` deep-link path, honoured for all logins after sign-in. Same
// round-trip survival pattern as pendingEnrollment.
const PENDING_NEXT_KEY = 'audirie.pendingNext';

const readPendingEnrollment = () => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_ENROLLMENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const storePendingEnrollment = (enrollment) => {
  try { window.sessionStorage.setItem(PENDING_ENROLLMENT_KEY, JSON.stringify(enrollment)); } catch (err) { /* best effort */ }
};

const clearPendingEnrollment = () => {
  try { window.sessionStorage.removeItem(PENDING_ENROLLMENT_KEY); } catch (err) { /* best effort */ }
};

const storePendingNext = (path) => {
  try { window.sessionStorage.setItem(PENDING_NEXT_KEY, path); } catch (err) { /* best effort */ }
};

const takePendingNext = () => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_NEXT_KEY);
    window.sessionStorage.removeItem(PENDING_NEXT_KEY);
    // Re-validate on the way out — sessionStorage is not a trust boundary.
    return sanitizeNextPath(raw);
  } catch (err) {
    return null;
  }
};

const ENROLL_ERROR_MESSAGES = {
  EXPIRED: 'This enrollment link has expired. Ask your administrator for a new one.',
  CAP_EXCEEDED: 'This enrollment link is no longer available.',
  RULE_DISABLED: 'This enrollment link is no longer available.',
  VERIFY_INVALID: 'This confirmation link is invalid or was already used. Open the enrollment page again to request a new email.',
  VERIFY_EXPIRED: 'This confirmation link has expired. Open the enrollment page again to request a new email.',
  LOGIN_FAILED: 'We confirmed your email but could not sign you in automatically. Sign in below, or request a new confirmation email from the enrollment page.',
  REVOKED: 'This enrollment link has been revoked. Ask your administrator for a new one.'
};
const ENROLL_ERROR_DEFAULT = 'This enrollment link is not valid.';

internals.loginRules = Joi.object({
  email: Joi.string().min(1),
  password: Joi.string().min(1)
});

export default class Profile extends TrackableReact {
  constructor(props) {
    super(props);
    const pendingSsoReturn = peekPendingSso();
    this.pendingSso = pendingSsoReturn;
    this.state = {
      login: {
        email: '',
        password: ''
      },
      redirect: {
        error: false,
        statusCode: 0,
      },
      pwreset: false,
      mode: pendingSsoReturn ? 'returning' : 'email',
      errs: null,
      saving: !!pendingSsoReturn,
      seenBefore: false,
      multiFactorResolver: null,
      ssoProviderId: null,
      providers: [],
      org: pendingSsoReturn?.orgName ? { name: pendingSsoReturn.orgName } : null,
      hint: null,
      accountBlockMessage: null,
      enrollError: null,
      platformStatus: null,
      statusChecking: false
    };

    this.login = this.login.bind(this);
    this.resolveSsoProvider = this.resolveSsoProvider.bind(this);
    this.beginSsoSignIn = this.beginSsoSignIn.bind(this);
    this.firebaseAuthentication = this.firebaseAuthentication.bind(this);
    this.updateInput = this.updateInput.bind(this);
    this.resetPW = this.resetPW.bind(this);
    this.pickProvider = this.pickProvider.bind(this);
    this.resetToEmail = this.resetToEmail.bind(this);
    this.continueAsHint = this.continueAsHint.bind(this);
    this.useDifferentAccount = this.useDifferentAccount.bind(this);
    this.completeSsoCredential = this.completeSsoCredential.bind(this);
    this.refreshPlatformStatus = this.refreshPlatformStatus.bind(this);
  }

  async refreshPlatformStatus() {
    this.setState({ statusChecking: true });
    try {
      const { data } = await Axios.get('/api/status', { timeout: 8000 });
      this.setState({
        platformStatus: {
          maintenanceEnabled: !!data?.maintenance?.enabled,
          maintenanceMessage: data?.maintenance?.message || null,
          coreOk: data?.core?.ok !== false
        },
        statusChecking: false
      });
    } catch (err) {
      // Leave prior status if any; don't invent a Core outage from a status fetch blip.
      this.setState({ statusChecking: false });
    }
  }

  async componentDidMount() {
    // Platform status first so maintenance can short-circuit the login flow.
    void this.refreshPlatformStatus();

    const urlParams = new URLSearchParams(window.location.search);
    const [error, status] = [urlParams.get('error'), urlParams.get('status')];
    if (error && status) this.setState({ redirect: { error, statusCode: status }});

    // Deep link: remember a safe `next` for after sign-in (all login types).
    const nextPath = sanitizeNextPath(urlParams.get('next'));
    if (nextPath) storePendingNext(nextPath);

    // A /enroll link that failed validation lands here with an error code.
    const enrollError = urlParams.get('enrollError');
    if (enrollError) this.setState({ enrollError });

    // A validated /enroll link lands here with a handle + SSO provider (SAML or OIDC).
    const enrollHandle = urlParams.get('enroll');
    const enrollProviderId = urlParams.get('provider');
    let enrollment = null;
    if (enrollHandle && enrollProviderId) {
      let dest = null;
      try {
        dest = urlParams.get('dest') ? JSON.parse(urlParams.get('dest')) : null;
      } catch (err) { /* malformed dest — enroll without a destination */ }
      enrollment = { handle: enrollHandle, providerId: enrollProviderId, dest };
      storePendingEnrollment({ handle: enrollHandle, dest });
    }

    let seenBefore = false;
    try {
      seenBefore = !!(window.localStorage.getItem(SEEN_FLAG) || window.localStorage.getItem('loginType'));
    } catch (err) { /* private mode — treat as first visit */ }
    this.setState({ seenBefore });

    try {
      const firebaseResponse = await getSsoRedirectResult();
      if (firebaseResponse?.type === 'credential') {
        this.pendingSso = readPendingSso() || this.pendingSso;
        this.setState({
          mode: 'returning',
          saving: true,
          org: this.pendingSso?.orgName ? { name: this.pendingSso.orgName } : null,
          accountBlockMessage: null,
          login: this.pendingSso?.email
            ? { ...this.state.login, email: this.pendingSso.email }
            : this.state.login
        });
        await this.completeSsoCredential(firebaseResponse.data.user);
        return;
      }
      if (firebaseResponse?.type === 'providerNotFound') {
        clearPendingSso();
        this.setState({ mode: 'sso-failed', saving: false });
        return;
      }
      if (this.pendingSso) {
        clearPendingSso();
        this.pendingSso = null;
        this.setState({ mode: 'sso-failed', saving: false, org: null });
        return;
      }
    } catch (err) {
      console.error('Unable to complete SSO sign-in.', err);
      clearPendingSso();
      this.pendingSso = null;
      this.setState({ mode: 'sso-failed', saving: false });
      return;
    }

    // Fresh enrollment link (not the redirect-return leg, handled above):
    // skip the email/password steps and go straight to the organisation's
    // IdP sign-in (SAML or OIDC) via the normal SSO handoff.
    if (enrollment) {
      this.pickProvider({ providerId: enrollment.providerId, orgId: null, orgName: null });
      return;
    }

    this.loadLoginHint();
  }

  async loadLoginHint() {
    try {
      const { data } = await Axios.get('/api/login/hint');
      if (!data?.email || this.state.mode !== 'email') return;
      this.setState({
        hint: data,
        mode: 'continue-as',
        login: { ...this.state.login, email: data.email },
        org: data.method === 'sso' && data.orgName
          ? { name: data.orgName, blurb: 'You\u2019ve signed in on this device before.' }
          : null
      });
    } catch (err) { /* no hint — the email step is already showing */ }
  }

  markSeen() {
    try { window.localStorage.setItem(SEEN_FLAG, '1'); } catch (err) { /* best effort */ }
  }

  async login(user) {
    // Firebase is the only login path: the session call always carries a
    // Firebase ID token. The former raw email+password fallback was removed
    // (Alpha no longer accepts it) — never send a password to /api/login.
    if (!user?.accessToken) {
      this.setState({ errs: 'Invalid password', saving: false, mode: 'login' });
      return;
    }

    const loginType = 'firebase';
    const loginPayload = { firebaseToken: user.accessToken };

    // Routing metadata for the "Continue as" hint cookie; Connect strips it
    // before proxying the session call and pairs it with the verified email.
    loginPayload.hint = this.pendingSso
      ? { method: 'sso', providerId: this.pendingSso.providerId, orgName: this.pendingSso.orgName }
      : { method: 'password' };

    // Auto-enrollment: hand the validated handle to Alpha with the session
    // call; Alpha performs the actual enrollment server-side.
    const pendingEnrollment = readPendingEnrollment();
    if (typeof pendingEnrollment?.handle === 'string' && pendingEnrollment.handle) {
      loginPayload.enrollmentHandle = pendingEnrollment.handle;
    }

    Axios.post('/api/login', loginPayload)
      .then(({ data }) => {
        clearPendingEnrollment();
        window.localStorage.setItem('loginType', loginType);
        clearPendingSso();
        this.markSeen();
        this.setState({ saving: false });
        this.identify(data.userId, data);

        if (this.loginStartTime) {
          const timeTaken = new Date().getTime() - this.loginStartTime.getTime();
          console.log(`[Login] Time to login: ${timeTaken} ms`);
          this.track('User Event Timed', { userEvent: 'login', timeTakenMs: timeTaken });
        }

        window.location = this.resolvePostLoginPath(data, pendingEnrollment);
      })
      .catch((err) => {
        clearPendingEnrollment();
        console.error(err);
        const message = extractLoginErrorMessage(err);
        const status = err.response?.status;
        const wasSsoReturn = this.state.mode === 'returning' || !!this.pendingSso;
        let failedMode = this.state.mode;
        if (wasSsoReturn) {
          failedMode = status === 403 ? 'sso-account-blocked' : 'sso-failed';
        }
        this.setState({
          errs: message,
          accountBlockMessage: status === 403 ? message : null,
          saving: false,
          mode: failedMode
        });
      });
  }

  /**
   * Where to land after a successful login, in priority order:
   * (1) a safe `next` deep link, (2) the enrollment destination — from
   * Alpha's login response or the stored enrollment — mapped to the page
   * the dashboard uses to open a simulation, (3) the dashboard.
   */
  resolvePostLoginPath(data, pendingEnrollment) {
    const nextPath = takePendingNext();
    if (nextPath) return nextPath;

    const destination = data?.enrollment?.destination || pendingEnrollment?.dest || null;
    const destinationPath = destinationToPath(destination);
    if (destinationPath) return destinationPath;

    return '/';
  }

  async firebaseAuthentication(e) {
    e.preventDefault();
    this.pendingSso = null;
    this.loginStartTime = new Date();

    const valErrs = internals.loginRules.validate(this.state.login);
    if (valErrs.error) return this.setState({ errs: 'form required' });

    this.setState({ saving: true, errs: null });
    const firebaseResponse = await signInToFirebase(this.state.login.email, this.state.login.password);

    // Firebase was temporarily unavailable (e.g. rate-limited). Do NOT fall
    // back to the raw password path — for Firebase-managed accounts that
    // returns a misleading "password is incorrect". Ask the user to retry.
    if (firebaseResponse?.type === 'transientError') {
      console.warn(`[Login] Firebase sign-in temporarily unavailable: ${firebaseResponse.code}`);
      this.setState({ saving: false, mode: 'login', errs: 'Login temporarily unavailable. Please try again.' });
      return;
    }

    // Firebase rejected the credentials (wrong password / unknown account).
    // There is no server-side password fallback any more, so report it here.
    if (!firebaseResponse) {
      this.setState({ saving: false, mode: 'login', errs: 'Invalid password' });
      return;
    }

    switch(firebaseResponse.type) {
      case 'multiFactor':
        this.setState({
          saving: false,
          mode: 'mfa',
          multiFactorResolver: firebaseResponse.data
        });
        break;
      case 'credential':
        this.login(firebaseResponse.data?.user || {});
        break;
      default:
        console.log('Unexpected response type');
    }
  }

  async resolveSsoProvider(e) {
    e.preventDefault();
    const email = this.state.login.email;
    if (!email) {
      this.setState({ errs: 'form required' });
      return;
    }

    this.lookupAndRoute(email);
  }

  continueAsHint() {
    // The hint renders instantly but never routes on its own: the lookup at
    // click time is the source of truth, with the remembered provider only
    // used to skip the picker when it's still valid.
    this.lookupAndRoute(this.state.hint.email, this.state.hint.providerId);
  }

  useDifferentAccount() {
    Axios.delete('/api/login/hint').catch(() => { /* cosmetic — cookie expires anyway */ });
    this.setState({ hint: null });
    this.resetToEmail();
  }

  async lookupAndRoute(email, preferredProviderId) {
    this.loginStartTime = new Date();
    this.setState({ saving: true, errs: null });

    const started = Date.now();
    const spinnerTimer = window.setTimeout(
      () => this.setState({ mode: 'detecting' }),
      LOOKUP_SPINNER_AFTER_MS
    );

    const routeWhenPadded = (route) => {
      const elapsed = Date.now() - started;
      const pad = elapsed > LOOKUP_SPINNER_AFTER_MS
        ? Math.max(0, LOOKUP_FLOOR_MS - elapsed)
        : 0;
      window.setTimeout(route, pad);
    };

    try {
      const { data } = await Axios.post('/api/sso/provider', { email }, { timeout: LOOKUP_TIMEOUT_MS });
      window.clearTimeout(spinnerTimer);
      routeWhenPadded(() => this.routeFromLookup(data, preferredProviderId));
    } catch (err) {
      window.clearTimeout(spinnerTimer);
      if (err.response?.status === 404 || err.code === 'ECONNABORTED') {
        routeWhenPadded(() => this.setState({ mode: 'login', saving: false, org: null }));
        return;
      }
      console.error('Unable to start SSO sign-in.', err);
      routeWhenPadded(() => this.setState({ mode: 'login', errs: null, saving: false, org: null }));
    }
  }

  routeFromLookup(data, preferredProviderId) {
    const providers = normaliseProviderOptions(data);

    if (providers.length === 0) {
      this.setState({ mode: 'login', saving: false, org: null, providers: [] });
      return;
    }

    const preferred = preferredProviderId
      && providers.find((provider) => provider.providerId === preferredProviderId);
    if (preferred) {
      this.pickProvider(preferred);
      return;
    }

    if (providers.length === 1) {
      this.pickProvider(providers[0]);
      return;
    }

    this.setState({ mode: 'picker', saving: false, providers, org: null });
  }

  pickProvider(provider) {
    const pending = {
      ...provider,
      email: this.state.login.email || null
    };
    this.pendingSso = pending;
    storePendingSso(pending);
    this.setState({
      mode: 'sso',
      ssoProviderId: provider.providerId,
      org: provider.orgName ? { name: provider.orgName } : null,
      saving: false
    }, () => {
      window.setTimeout(this.beginSsoSignIn, SSO_REDIRECT_DELAY_MS);
    });
  }

  async completeSsoCredential(user) {
    const intendedEmail = this.pendingSso?.email || this.state.login.email;
    if (shouldRejectSsoEmailMismatch(intendedEmail, user?.email)) {
      clearPendingSso();
      this.pendingSso = null;
      try {
        await signOutOfFirebase();
      } catch (err) {
        console.warn('Firebase sign-out failed after SSO email mismatch.', err);
      }
      this.setState({ mode: 'sso-email-mismatch', saving: false });
      return;
    }
    this.login(user);
  }

  async beginSsoSignIn() {
    this.setState({ saving: true });
    try {
      const firebaseResponse = await signInWithSso(this.state.ssoProviderId);
      if (firebaseResponse?.type === 'credential') {
        this.setState({ mode: 'returning' });
        await this.completeSsoCredential(firebaseResponse.data.user);
      }
      if (firebaseResponse?.type === 'providerNotFound') {
        this.setState({ mode: 'sso-failed', saving: false });
      }
      if (firebaseResponse?.type === 'popupBlocked') {
        this.setState({ mode: 'sso', errs: 'popup blocked', saving: false });
      }
    } catch (err) {
      console.error('Unable to start SSO sign-in.', err);
      this.setState({ mode: 'sso-failed', saving: false });
    }
  }

  resetPW(e) {
    e.preventDefault();
    if (!this.state.login.email) {
      this.setState({ pwreset: 'need email' });
      return;
    }

    this.setState({ saving: true });
    Axios.post('/api/pwreset', this.state.login)
      .then(() => {
        this.setState({
          pwreset: 'sent',
          saving: false,
          login: { email: '', password: '' }
        });
      })
      .catch(err => {
        console.log('An error occurred while resetting the password');
        this.setState({ pwreset: err.response.data, saving: false });
      });
  }

  updateInput(e) {
    const login = { ...this.state.login, [e.target.name]: e.target.value };
    this.setState({ login, errs: null });
  }

  resetToEmail() {
    this.pendingSso = null;
    clearPendingSso();
    this.setState({
      mode: 'email',
      errs: null,
      accountBlockMessage: null,
      saving: false,
      org: null,
      providers: [],
      ssoProviderId: null,
      login: { email: '', password: '' }
    });
  }

  renderLoginContent() {
    const {
      accountBlockMessage,
      errs,
      hint,
      login,
      mode,
      multiFactorResolver,
      org,
      platformStatus,
      providers,
      pwreset,
      saving,
      seenBefore,
      statusChecking
    } = this.state;

    if (platformStatus?.maintenanceEnabled) {
      return (
        <MaintenanceStep
          message={platformStatus.maintenanceMessage}
          checking={statusChecking}
          onCheckAgain={this.refreshPlatformStatus}
        />
      );
    }

    const softOutage = platformStatus && platformStatus.coreOk === false;
    const softBanner = softOutage ? <SoftOutageBanner /> : null;

    switch(mode) {
      case 'continue-as':
        return (
          <>
            {softBanner}
            <ContinueAsStep hint={hint} onContinue={this.continueAsHint} onDifferent={this.useDifferentAccount} />
          </>
        );
      case 'forgot-pw':
        return (
          <>
            {softBanner}
            <PasswordResetForm email={login.email} result={pwreset} saving={saving} onSubmit={this.resetPW} onInputChange={this.updateInput} onCancel={() => this.setState({ mode: 'login' })} />
          </>
        );
      case 'mfa':
        return <SecondaryAuthentication resolver={multiFactorResolver} onEnd={(userCredential) => this.login(userCredential?.user)} />;
      case 'login':
        return (
          <>
            {softBanner}
            <PasswordLoginForm login={login} error={errs} saving={saving} onSubmit={this.firebaseAuthentication} onInputChange={this.updateInput} onBack={() => this.setState({ mode: 'email', errs: null, org: null, login: { ...login, password: '' } })} onForgotPassword={() => this.setState({ mode: 'forgot-pw' })} />
          </>
        );
      case 'detecting':
        return <DetectingStep />;
      case 'picker':
        return (
          <>
            {softBanner}
            <OrgPickerStep email={login.email} providers={providers} onPick={this.pickProvider} onBack={this.resetToEmail} />
          </>
        );
      case 'sso':
        return <SsoRedirect org={org} error={errs} onBack={this.resetToEmail} />;
      case 'returning':
        return <ReturningStep />;
      case 'sso-account-blocked':
        return <SsoAccountBlocked org={org} message={accountBlockMessage || errs} onGoBack={this.resetToEmail} />;
      case 'sso-email-mismatch':
        return <SsoEmailMismatch org={org} onGoBack={this.resetToEmail} />;
      case 'sso-failed':
        return <SsoFailure org={org} onGoBack={this.resetToEmail} />;
      default:
        return (
          <>
            {softBanner}
            <EmailLoginForm
              login={login}
              error={errs}
              saving={saving}
              seenBefore={seenBefore}
              onSubmit={this.resolveSsoProvider}
              onInputChange={this.updateInput}
              onBack={this.resetToEmail}
              disabled={softOutage}
            />
          </>
        );
    }
  }

  render() {
    // Failed `?aid=` token login lands here with the original query reattached
    // (incl. empty `return=` + cookie-auth's `next=`). Only bounce to a real
    // org failure URL — never treat `&next=…` as a redirect (that produced
    // nginx 404s on `/&next=/`).
    if (location.search?.indexOf('aid') > -1) {
      const returnUrl = resolveAidFailureReturnUrl(location.search);
      if (returnUrl) {
        window.location.href = returnUrl;
        return;
      }
      // Empty/missing/unsafe return: show the login form, but surface a clear
      // magic-link failure when enrollError was not already set by the redirect.
      if (!this.state.enrollError) {
        this.state.enrollError = 'LOGIN_FAILED';
      }
    }

    return (
      <div className="aud-login-page">
        <div className="aud-toast-holder">
          <ErrorToast error={this.state.redirect.error} statusCode={this.state.redirect.statusCode} />
        </div>
        <LoginCard org={this.state.org}>
          {this.state.enrollError && (
            <div className="aud-banner aud-banner-error text-danger">
              <p>{ENROLL_ERROR_MESSAGES[this.state.enrollError] || ENROLL_ERROR_DEFAULT}</p>
            </div>
          )}
          {this.renderLoginContent()}
        </LoginCard>
      </div>
    );
  }
}
